"use client";
import { useQuery } from "@tanstack/react-query";
import { usePathname } from "next/navigation";

// Tipo da resposta da API de refresh
interface RefreshResponse {
  access_token: string;
  refresh_token: string;
  scope: string;
  token_type: string;
  expires_in: number;
  hasMFA: boolean;
}

// Função para refresh que pode ser usada tanto no client quanto no server
export async function refreshAuthToken(refreshToken: string) {
  const { apiClient, post } = await import("@/_nucleo/api/client");
  const url = `api/oauth2/v1/token?grant_type=refresh_token&refresh_token=${refreshToken}`;
  const response = await post<RefreshResponse>(apiClient.production, url);

  const apiData = response.data;
  const expiresAt = new Date();
  expiresAt.setSeconds(expiresAt.getSeconds() + apiData.expires_in);

  return {
    token: apiData.access_token,
    refreshToken: apiData.refresh_token,
    expiresAt: expiresAt,
  };
}

// Hook TanStack Query para refresh automático
export function useRefreshAuthTokenQuery(
  authData: {
    token: string;
    refreshToken: string;
    expiresAt: Date;
  },
  enabled = true
) {
  const pathname = usePathname();

  // Só habilita a query se não estiver na /login
  const shouldEnable = enabled && pathname !== "/login";

  return useQuery({
    queryKey: ["refresh-auth-token", authData.token, authData.refreshToken],
    queryFn: async () => {
      const expiresAt = new Date(authData.expiresAt).getTime();
      const now = Date.now();
      // Se expira em menos de 5 min, faz refresh
      if (expiresAt - now <= 5 * 60 * 1000) {
        return refreshAuthToken(authData.refreshToken);
      }
      // Senão, retorna o token atual
      return {
        token: authData.token,
        refreshToken: authData.refreshToken,
        expiresAt: authData.expiresAt,
      };
    },
    enabled: shouldEnable,
    refetchOnWindowFocus: true, // ✅ Verifica quando volta ao foco
    refetchInterval: 4 * 60 * 1000, // ✅ A cada 4 min (mais frequente)
    refetchIntervalInBackground: true, // ✅ ✅ CRUCIAL: Funciona mesmo com aba inativa
  });
}
